<template>
  <div class="ai-chat-page">
    <vue-header />

    <div class="ai-chat-shell">
      <div class="ai-chat-layout">
        <aside class="chat-sidebar" :class="{ hidden: !isSidebarVisible }">
          <div class="sidebar-head">
            <h5>Workman's Assistant</h5>
            <button class="icon-btn close-menu-btn" @click="closeSidebar" title="Close menu">
              <i class="material-icons">close</i>
            </button>
          </div>

          <div class="sidebar-divider"></div>

          <button class="new-chat-btn" @click="createNewChat" :disabled="isCreatingChat">
            <i class="material-icons">add</i>
            <span>{{ isCreatingChat ? 'Creating chat...' : 'New Chat' }}</span>
          </button>

          <div class="search-wrap">
            <input
              v-model="searchTerm"
              type="text"
              placeholder="Search conversations..."
              @input="onSearchInput"
            />
            <i class="material-icons search-icon">search</i>
          </div>

          <div class="chat-history" v-if="!isLoadingHistory">
            <template v-for="group in groupedChatList">
              <div v-if="group.items.length" :key="group.key" class="chat-group">
                <p class="group-title">{{ group.title }}</p>
                <div
                  v-for="chat in group.items"
                  :key="chat.id"
                  class="chat-item"
                  :class="{ active: currentChatId === chat.id }"
                  @click="selectChat(chat.id)"
                >
                  <!-- <i class="material-icons">chat</i> -->
                  <span class="chat-title">{{ truncateTitle(chat.title || 'New Chat') }}</span>
                  <button class="delete-chat" @click.stop="deleteChat(chat.id)">
                    <i class="material-icons">delete</i>
                  </button>
                </div>
              </div>
            </template>

            <p v-if="!filteredChats.length" class="empty-history">No conversations found.</p>

            <button
              v-if="!searchTerm.trim() && hasMoreChats"
              class="load-more-btn"
              @click="loadMoreChats"
              :disabled="isLoadingMore"
            >
              <i v-if="isLoadingMore" class="fa fa-spinner fa-spin"></i>
              <span>{{ isLoadingMore ? 'Loading more...' : 'Load More' }}</span>
            </button>
          </div>
          <div v-else class="history-loading-card">
            <i class="fa fa-spinner fa-spin"></i>
            <span>Loading conversations...</span>
          </div>
        </aside>

        <section class="chat-main">
          <div class="chat-header">
            <button class="icon-btn" @click="toggleSidebar">
              <i class="material-icons">{{ isSidebarVisible ? 'chevron_left' : 'menu' }}</i>
            </button>
            <h5>{{ currentChatTitle }}</h5>
          </div>

          <div class="chat-body" ref="chatContainer">
            <div v-if="!currentChatMessages.length && !isLoading" class="empty-chat">
              <i class="material-icons">smart_toy</i>
              <p>How can I help you today?</p>
            </div>

            <div
              v-for="(message, idx) in currentChatMessages"
              :key="idx"
              class="message-row"
              :class="message.sender === 'user' ? 'user' : 'ai'"
              :data-message-id="message._id || ''"
            >
              <div class="message-content" :class="{ 'table-message': isTabularMessage(message) }">
                <div class="message-text" v-html="resolveMessageText(message)"></div>
                <div class="message-footer">
                  <span>{{ formatTime(message.timestamp) }}</span>
                  <template v-if="message.sender === 'ai'">
                    <div class="message-feedback">
                      <button
                        class="feedback"
                        :class="{ active: message.feedback === 'like' }"
                        @click="provideFeedback(message, 'like')"
                      >
                        <i class="material-icons">thumb_up</i>
                      </button>
                      <button
                        class="feedback"
                        :class="{ active: message.feedback === 'dislike' }"
                        @click="provideFeedback(message, 'dislike')"
                      >
                        <i class="material-icons">thumb_down</i>
                      </button>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="isLoading" class="typing">
              <span></span><span></span><span></span>
            </div>
          </div>

          <div class="chat-input-wrap">
            <textarea
              v-model="userInput"
              placeholder="Type your message here..."
              rows="1"
              ref="textareaRef"
              @keydown="handleKeydown"
              @input="autoResizeTextarea"
            ></textarea>
            <button class="icon-btn send" @click="sendMessage" :disabled="isLoading || !userInput.trim()">
              <i class="material-icons">send</i>
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import CommonConstants from '@/constants/constants.js'
import Vue from 'vue'

export default {
  name: 'AiChat',
  data() {
    return {
      constants: CommonConstants,
      chatSessions: [],
      currentChatId: null,
      userInput: '',
      isLoading: false,
      isLoadingHistory: false,
      isLoadingMore: false,
      isSidebarVisible: window.innerWidth > 768,
      searchTerm: '',
      searchDebounceTimer: null,
      searchResults: [],
      isSearching: false,
      isCreatingChat: false,
      isRecording: false,
      recognition: null,
      currentPage: 1,
      pageSize: 10,
      totalChats: 0,
      hasMoreChats: true,
      aiTableActionHandler: null,
    }
  },
  computed: {
    userId() {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      return userData.id || null
    },
    currentChat() {
      return this.chatSessions.find(chat => chat.id === this.currentChatId) || null
    },
    currentChatMessages() {
      if (!this.currentChat || !Array.isArray(this.currentChat.messages)) return []
      return this.currentChat.messages
    },
    currentChatTitle() {
      if (!this.currentChat) return 'New Conversation'
      return this.currentChat.title || 'New Conversation'
    },
    filteredChats() {
      if (!this.searchTerm.trim()) return this.chatSessions
      return this.searchResults
    },
    groupedChatList() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      const prevWeek = new Date(today)
      prevWeek.setDate(prevWeek.getDate() - 7)

      const prevMonth = new Date(today)
      prevMonth.setDate(prevMonth.getDate() - 30)

      const groups = {
        today: [],
        yesterday: [],
        previousWeek: [],
        previousMonth: [],
        older: [],
      }

      const sorted = [...this.filteredChats].sort((a, b) => this.getChatDate(b) - this.getChatDate(a))

      sorted.forEach(chat => {
        const chatDate = this.getChatDate(chat)
        chatDate.setHours(0, 0, 0, 0)

        if (chatDate.getTime() === today.getTime()) groups.today.push(chat)
        else if (chatDate.getTime() === yesterday.getTime()) groups.yesterday.push(chat)
        else if (chatDate >= prevWeek) groups.previousWeek.push(chat)
        else if (chatDate >= prevMonth) groups.previousMonth.push(chat)
        else groups.older.push(chat)
      })

      return [
        { key: 'today', title: 'Today', items: groups.today },
        { key: 'yesterday', title: 'Yesterday', items: groups.yesterday },
        { key: 'previousWeek', title: 'Previous 7 Days', items: groups.previousWeek },
        { key: 'previousMonth', title: 'Previous 30 Days', items: groups.previousMonth },
        { key: 'older', title: 'Older', items: groups.older },
      ]
    },
  },
  created() {
    this.fetchChats()
  },
  mounted() {
    this.bindAiTableActions()
  },
  updated() {
    this.$nextTick(() => {
      this.bindTablePagination()
    })
  },
  beforeDestroy() {
    this.unbindAiTableActions()
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    if (this.recognition) this.recognition.stop()
  },
  methods: {
    apiHeaders() {
      return {
        'Content-Type': 'application/json',
        'API-Key': this.constants.ai_service_api_key || '',
      }
    },
    getChatDate(chat) {
      if (chat.updated_at) return new Date(chat.updated_at)
      if (chat.created_at) return new Date(chat.created_at)
      if (chat.timestamp) return new Date(chat.timestamp)
      if (chat.messages && chat.messages.length && chat.messages[chat.messages.length - 1].timestamp) {
        return new Date(chat.messages[chat.messages.length - 1].timestamp)
      }
      return new Date()
    },
    resolveMessageText(message) {
      if (!message) return ''
      if (message.text && message.text.response) return message.text.response
      return message.text || ''
    },
    isTabularMessage(message) {
      const html = this.resolveMessageText(message)
      return typeof html === 'string' && (html.indexOf('<table') !== -1 || html.indexOf('ai-table') !== -1)
    },
    bindAiTableActions() {
      const root = this.$refs.chatContainer
      if (!root || this.aiTableActionHandler) return

      this.aiTableActionHandler = event => {
        const actionEl = event.target && event.target.closest ? event.target.closest('button, a') : null
        if (!actionEl) return

        const insideAiTable = !!actionEl.closest('.ai-table-container')
        if (!insideAiTable) return

        const label = ((actionEl.textContent || '').trim().toLowerCase())
        const isDownload =
          actionEl.id === 'download-ai-table' ||
          actionEl.classList.contains('download-ai-table') ||
          label.includes('download csv') ||
          (label.includes('download') && label.includes('csv'))

        if (!isDownload) return

        event.preventDefault()
        event.stopPropagation()
        this.downloadCsvForElement(actionEl)
      }

      root.addEventListener('click', this.aiTableActionHandler, true)
    },
    unbindAiTableActions() {
      const root = this.$refs.chatContainer
      if (root && this.aiTableActionHandler) {
        root.removeEventListener('click', this.aiTableActionHandler, true)
      }
      this.aiTableActionHandler = null
    },
    resolveMessageIdForAiTable(element) {
      const container = element.closest('.ai-table-container')
      const row = element.closest('.message-row')
      let messageId =
        element.dataset.messageId ||
        element.getAttribute('data-message-id') ||
        (container && (container.dataset.messageId || container.getAttribute('data-message-id'))) ||
        (row && row.dataset.messageId) ||
        ''

      if (messageId) return messageId

      const aiRows = Array.from(this.$el.querySelectorAll('.message-row.ai'))
      const rowIndex = aiRows.indexOf(row)
      if (rowIndex < 0) return ''

      const aiMessages = this.currentChatMessages.filter(msg => msg && msg.sender === 'ai')
      const fallbackMessage = aiMessages[rowIndex]
      return (fallbackMessage && fallbackMessage._id) || ''
    },
    async downloadCsvForElement(element) {
      const messageId = this.resolveMessageIdForAiTable(element)
      if (!messageId || !this.constants.ai_service_api_url) {
        Vue.$toast.error('Could not find the message for CSV export', { position: 'top' })
        return
      }

      const originalHtml = element.innerHTML
      element.disabled = true
      element.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Generating CSV...'

      try {
        const response = await axios.post(
          `${this.constants.ai_service_api_url}/export-csv`,
          { message_id: messageId },
          {
            headers: this.apiHeaders(),
            responseType: 'blob',
          }
        )

        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        const d = new Date()
        const fileName = `query_results_${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}_${d.getHours()}-${d.getMinutes()}.csv`

        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        Vue.$toast.success('CSV downloaded successfully', { position: 'top' })
      } catch (error) {
        Vue.$toast.error('Failed to download CSV', { position: 'top' })
      } finally {
        element.disabled = false
        element.innerHTML = originalHtml
      }
    },
    bindTablePagination() {
      if (!this.$el) return

      const containers = this.$el.querySelectorAll('.ai-table-container')
      if (!containers.length) return

      containers.forEach(container => {
        if (container.dataset.paginationBound === 'true') return

        const prevBtn = container.querySelector('.prev-page')
        const nextBtn = container.querySelector('.next-page')
        if (!prevBtn && !nextBtn) return

        const pageSize = parseInt(container.dataset.pageSize || container.dataset.page_size || '25', 10)
        let currentPage = parseInt(container.dataset.page || '1', 10)
        let totalCount = parseInt(container.dataset.totalCount || container.dataset.total_count || '0', 10)
        let totalPages = parseInt(container.dataset.totalPages || container.dataset.total_pages || '0', 10)

        if (!totalPages && pageSize > 0 && totalCount > 0) {
          totalPages = Math.ceil(totalCount / pageSize)
        }

        const findMessageId = () => {
          return (
            container.dataset.messageId ||
            container.getAttribute('data-message-id') ||
            (prevBtn && (prevBtn.dataset.messageId || prevBtn.getAttribute('data-message-id'))) ||
            (nextBtn && (nextBtn.dataset.messageId || nextBtn.getAttribute('data-message-id'))) ||
            (container.closest('.message-row') && container.closest('.message-row').dataset.messageId) ||
            ''
          )
        }

        const renderRows = (rows, columns) => {
          const tbody = container.querySelector('.table-body, tbody')
          if (!tbody) return

          if (!Array.isArray(rows) || !rows.length) {
            tbody.innerHTML = '<tr><td colspan="100" class="text-center">No data found</td></tr>'
            return
          }

          const safeColumns = Array.isArray(columns) && columns.length ? columns : Object.keys(rows[0])
          tbody.innerHTML = rows
            .map(row => `<tr>${safeColumns.map(col => `<td>${row[col] !== null && row[col] !== undefined ? row[col] : ''}</td>`).join('')}</tr>`)
            .join('')
        }

        const updatePaginationUI = () => {
          const showing = container.querySelector('.ai-table-showing')
          const pageInfo = container.querySelector('.page-info')

          if (totalCount > 0 && pageSize > 0) {
            const start = (currentPage - 1) * pageSize + 1
            const end = Math.min(currentPage * pageSize, totalCount)
            if (showing) showing.textContent = `Showing ${start}-${end} of ${totalCount} records`
          }

          if (pageInfo && totalPages > 0) {
            pageInfo.textContent = `Page ${currentPage} of ${totalPages}`
          }

          if (prevBtn) prevBtn.disabled = currentPage <= 1
          if (nextBtn) nextBtn.disabled = totalPages > 0 ? currentPage >= totalPages : false
        }

        const fetchPage = async page => {
          const messageId = findMessageId()
          if (!messageId || !this.constants.ai_service_api_url) return

          try {
            const response = await axios.post(
              `${this.constants.ai_service_api_url}/table-page`,
              {
                message_id: messageId,
                page,
                page_size: pageSize,
              },
              { headers: this.apiHeaders() }
            )

            const payload = response && response.data ? response.data : null
            if (!payload || payload.error) return

            const rows = payload.data || []
            const columns = payload.columns || []
            const pagination = payload.pagination || {}

            currentPage = pagination.page || page
            totalCount = pagination.total_count !== undefined ? pagination.total_count : totalCount
            totalPages = pagination.total_pages !== undefined ? pagination.total_pages : totalPages

            container.dataset.page = String(currentPage)
            container.dataset.totalCount = String(totalCount)
            container.dataset.totalPages = String(totalPages)

            renderRows(rows, columns)
            updatePaginationUI()
          } catch (error) {
          }
        }

        if (prevBtn) {
          prevBtn.onclick = null
          prevBtn.addEventListener('click', event => {
            event.preventDefault()
            if (currentPage > 1) fetchPage(currentPage - 1)
          })
        }

        if (nextBtn) {
          nextBtn.onclick = null
          nextBtn.addEventListener('click', event => {
            event.preventDefault()
            if (totalPages === 0 || currentPage < totalPages) fetchPage(currentPage + 1)
          })
        }

        updatePaginationUI()
        container.dataset.paginationBound = 'true'
      })
    },
    truncateTitle(title) {
      if (!title) return ''
      return title.length > 30 ? `${title.slice(0, 30)}...` : title
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    toggleSidebar() {
      this.isSidebarVisible = !this.isSidebarVisible
    },
    closeSidebar() {
      this.isSidebarVisible = false
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight
        }
      })
    },
    autoResizeTextarea() {
      const textarea = this.$refs.textareaRef
      if (!textarea) return
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 180)}px`
    },
    handleKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },
    async fetchChats() {
      if (!this.userId || !this.constants.ai_service_api_url) return

      this.isLoadingHistory = true
      try {
        const response = await axios.get(`${this.constants.ai_service_api_url}/chats`, {
          params: {
            user_id: this.userId,
            page: 1,
            limit: this.pageSize,
          },
          headers: this.apiHeaders(),
        })

        this.chatSessions = (response.data && response.data.chats) || []
        this.totalChats = (response.data && response.data.total) || this.chatSessions.length
        this.currentPage = 1
        this.hasMoreChats = this.chatSessions.length < this.totalChats

        if (!this.chatSessions.length) {
          await this.createNewChat(true)
        } else {
          this.currentChatId = null
        }
      } catch (error) {
        Vue.$toast.error('Failed to load conversations', { position: 'top' })
      } finally {
        this.isLoadingHistory = false
      }
    },
    async createNewChat(silent) {
      if (!this.userId || !this.constants.ai_service_api_url) return

      this.isCreatingChat = true
      try {
        const response = await axios.post(
          `${this.constants.ai_service_api_url}/chats/create`,
          {
            user_id: this.userId,
            title: 'New Chat',
            from_button: true,
          },
          { headers: this.apiHeaders() }
        )

        if (response.data && response.data.id) {
          this.chatSessions.unshift({
            ...response.data,
            messages: response.data.messages || [],
          })
          this.totalChats = this.totalChats + 1
          this.hasMoreChats = this.chatSessions.length < this.totalChats
          this.currentChatId = response.data.id
          this.searchTerm = ''
          this.searchResults = []
          if (window.innerWidth <= 768) this.isSidebarVisible = false
          this.scrollToBottom()
        }
      } catch (error) {
        if (!silent) Vue.$toast.error('Failed to create chat', { position: 'top' })
      } finally {
        this.isCreatingChat = false
      }
    },
    selectChat(chatId) {
      this.currentChatId = chatId
      this.scrollToBottom()
      if (window.innerWidth <= 768) this.isSidebarVisible = false
    },
    async deleteChat(chatId) {
      if (!confirm('Are you sure you want to delete this chat?')) return

      try {
        await axios.delete(`${this.constants.ai_service_api_url}/chats/${chatId}/delete`, {
          headers: this.apiHeaders(),
        })
      } catch (error) {
      }

      this.chatSessions = this.chatSessions.filter(chat => chat.id !== chatId)
      this.totalChats = Math.max(0, this.totalChats - 1)
      this.hasMoreChats = this.chatSessions.length < this.totalChats

      if (this.currentChatId === chatId) {
        if (this.chatSessions.length) this.currentChatId = this.chatSessions[0].id
        else this.createNewChat(true)
      }
    },
    async loadMoreChats() {
      if (this.isLoadingMore || !this.hasMoreChats || !this.userId || !this.constants.ai_service_api_url) return

      this.isLoadingMore = true
      try {
        const nextPage = this.currentPage + 1
        const response = await axios.get(`${this.constants.ai_service_api_url}/chats`, {
          params: {
            user_id: this.userId,
            page: nextPage,
            limit: this.pageSize,
          },
          headers: this.apiHeaders(),
        })

        const newChats = (response.data && response.data.chats) || []
        const existingIds = new Set(this.chatSessions.map(chat => chat.id))
        const mergedChats = newChats.filter(chat => !existingIds.has(chat.id))

        this.chatSessions = this.chatSessions.concat(mergedChats)
        this.totalChats = (response.data && response.data.total) || this.totalChats
        this.currentPage = nextPage
        this.hasMoreChats = this.chatSessions.length < this.totalChats
      } catch (error) {
        Vue.$toast.error('Failed to load more conversations', { position: 'top' })
      } finally {
        this.isLoadingMore = false
      }
    },
    async onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)

      if (!this.searchTerm.trim()) {
        this.searchResults = []
        return
      }

      this.searchDebounceTimer = setTimeout(async () => {
        this.isSearching = true
        try {
          const response = await axios.get(`${this.constants.ai_service_api_url}/chats/search`, {
            params: {
              user_id: this.userId,
              query: this.searchTerm.trim(),
              limit: 50,
            },
            headers: this.apiHeaders(),
          })
          this.searchResults = (response.data && response.data.chats) || []
        } catch (error) {
          this.searchResults = []
        } finally {
          this.isSearching = false
        }
      }, 250)
    },
    async provideFeedback(message, feedbackType) {
      if (!message || !message._id) return
      try {
        await axios.put(
          `${this.constants.ai_service_api_url}/messages/${message._id}/feedback`,
          { feedback: feedbackType, user_id: this.userId },
          { headers: this.apiHeaders() }
        )
        message.feedback = feedbackType
      } catch (error) {
        Vue.$toast.error('Failed to submit feedback', { position: 'top' })
      }
    },
    async sendMessage() {
      if (!this.userInput.trim() || this.isLoading) return
      if (!this.currentChatId) await this.createNewChat(true)

      const userMessage = this.userInput.trim()
      const chatIndex = this.chatSessions.findIndex(chat => chat.id === this.currentChatId)
      if (chatIndex === -1) return

      this.userInput = ''
      this.autoResizeTextarea()

      if (!Array.isArray(this.chatSessions[chatIndex].messages)) {
        this.$set(this.chatSessions[chatIndex], 'messages', [])
      }

      this.chatSessions[chatIndex].messages.push({
        sender: 'user',
        text: userMessage,
        timestamp: new Date().toISOString(),
      })
      this.scrollToBottom()

      this.isLoading = true
      try {
        const response = await axios.post(
          `${this.constants.ai_service_api_url}/chats/${this.currentChatId}/messages/add`,
          {
            sender: 'user',
            text: userMessage,
            user_id: this.userId,
          },
          { headers: this.apiHeaders() }
        )

        if (response.data && response.data.user_message && response.data.ai_message) {
          const len = this.chatSessions[chatIndex].messages.length
          this.$set(this.chatSessions[chatIndex].messages, len - 1, response.data.user_message)
          this.chatSessions[chatIndex].messages.push(response.data.ai_message)

          if (response.data.generated_title) {
            this.$set(this.chatSessions[chatIndex], 'title', response.data.generated_title)
          }
        } else {
          const fallback = await axios.post(
            `${this.constants.ai_service_api_url}/query`,
            {
              question: userMessage,
              user_id: this.userId,
              thread_id: this.currentChatId,
              skip_message_storage: false,
            },
            { headers: this.apiHeaders() }
          )

          this.chatSessions[chatIndex].messages.push({
            sender: 'ai',
            text: (fallback.data && (fallback.data.response || fallback.data)) || 'No response',
            timestamp: new Date().toISOString(),
          })
        }
      } catch (error) {
        this.chatSessions[chatIndex].messages.push({
          sender: 'ai',
          text: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date().toISOString(),
        })
        Vue.$toast.error('Failed to send message', { position: 'top' })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },
    toggleVoiceRecognition() {
      if (this.isRecording) {
        if (this.recognition) this.recognition.stop()
        this.isRecording = false
        return
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SpeechRecognition) {
        Vue.$toast.error('Speech recognition is not supported in this browser', { position: 'top' })
        return
      }

      this.recognition = new SpeechRecognition()
      this.recognition.continuous = false
      this.recognition.interimResults = true
      this.recognition.lang = 'en-US'

      this.recognition.onresult = event => {
        const transcript = Array.from(event.results).map(r => r[0].transcript).join('')
        this.userInput = transcript
        this.$nextTick(this.autoResizeTextarea)
      }

      this.recognition.onend = () => {
        this.isRecording = false
      }

      this.recognition.onerror = () => {
        this.isRecording = false
      }

      this.recognition.start()
      this.isRecording = true
    },
  },
}
</script>

<style scoped>
.ai-chat-page {
  min-height: 100vh;
  background: #f4f7fb;
}

.ai-chat-shell {
  padding: 8px;
}

.ai-chat-layout {
  display: flex;
  gap: 10px;
  height: calc(100vh - 78px);
}

.chat-sidebar {
  width: 300px;
  flex-shrink: 0;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #f4f4f4;
  padding: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chat-sidebar.hidden {
  display: none;
}

.sidebar-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.sidebar-head h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #1f2f40;
}

.sidebar-divider {
  height: 1px;
  background: #d7d7d7;
  margin: 8px 0 10px;
}

.new-chat-btn {
  border: 0;
  background: transparent;
  color: #1f2f40;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 500;
  padding: 6px 2px 8px;
  text-align: left;
}

.new-chat-btn:disabled {
  opacity: 0.7;
}

.search-wrap {
  position: relative;
}

.search-wrap input {
  width: 100%;
  border: 1px solid #d2d8df;
  border-radius: 6px;
  padding: 9px 32px 9px 10px;
  font-size: 14px;
  color: #344f69;
  background: #f7f8fa;
}

.search-icon {
  position: absolute;
  right: 8px;
  top: 38%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #8398ad;
}

.chat-history {
  margin-top: 12px;
  overflow-y: auto;
}

.group-title {
  margin: 10px 0 6px;
  color: #6c7d90;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
}

.chat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
}

.chat-item:hover {
  background: #eef3f9;
}

.chat-item.active {
  background: #e3edf8;
}

.chat-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  text-align: left;
}

.delete-chat {
  border: 0;
  background: transparent;
  color: #d35b5b;
  padding: 0;
}

.load-more-btn {
  width: 100%;
  margin-top: 10px;
  border: 1px solid #d2d8df;
  border-radius: 8px;
  background: #ffffff;
  color: #4a6784;
  font-size: 13px;
  font-weight: 500;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.load-more-btn:disabled {
  opacity: 0.75;
}

.chat-main {
  flex: 1;
  border: 1px solid #dce3ed;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  border-bottom: 1px solid #e6edf6;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.chat-header .icon-btn {
  flex-shrink: 0;
}

.chat-header .icon-btn .material-icons {
  font-size: 20px;
  line-height: 1;
}

.chat-header h5 {
  margin: 0;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-body {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
  background: #f8fbff;
}

.empty-chat {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #69819b;
}

.empty-chat i {
  font-size: 36px;
}

.message-row {
  display: flex;
  margin-bottom: 10px;
}

.message-row.user {
  justify-content: flex-end;
}

.message-row.ai {
  justify-content: flex-start;
}

.message-content {
  max-width: 86%;
  border-radius: 10px;
  padding: 10px;
  font-size: 13px;
  word-break: normal;
  overflow-wrap: anywhere;
  text-align: left;
}

.message-content.table-message {
  max-width: 100%;
}

.message-row.user .message-content {
  background: #4883ae;
  color: #fff;
}

.message-row.ai .message-content {
  background: #e8eff7;
  color: #1d2a39;
}

.message-footer {
  margin-top: 6px;
  font-size: 11px;
  display: flex;
  gap: 4px;
  align-items: center;
}

.message-feedback {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.feedback {
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.65;
  padding: 0;
}

.feedback.active {
  opacity: 1;
}

.typing {
  display: inline-flex;
  gap: 4px;
  padding: 8px 10px;
  background: #e8eff7;
  border-radius: 8px;
}

.typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #5d7288;
  animation: blink 1.2s infinite;
}

.typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}

.chat-input-wrap {
  border-top: 1px solid #e6edf6;
  padding: 8px;
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.chat-input-wrap textarea {
  flex: 1;
  border: 1px solid #d6dde8;
  border-radius: 8px;
  padding: 8px;
  resize: none;
  max-height: 180px;
  overflow-y: auto;
  font-size: 13px;
}

.icon-btn {
  border: 0;
  background: #eef3f9;
  color: #34506d;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-btn.send {
  background: #4883ae;
  color: #fff;
  margin-bottom: 25px;
}

.icon-btn.send[disabled] {
  opacity: 0.5;
}

.icon-btn.mic.recording {
  background: #d85151;
  color: #fff;
}

.empty-history {
  margin-top: 12px;
  color: #6c7d90;
  font-size: 13px;
}

.history-loading-card {
  margin-top: 76px;
  background: #fff;
  border: 1px solid #e7e8eb;
  border-radius: 10px;
  color: #547799;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  padding: 12px;
  font-size: 16px;
}

.close-menu-btn {
  background: transparent;
  color: #607b93;
}

.message-text :deep(.ai-table),
.message-text :deep(table.ai-table) {
  display: table !important;
  width: max-content !important;
  min-width: 100% !important;
  max-width: none !important;
  border-collapse: collapse;
  margin-top: 8px;
  table-layout: auto !important;
}

.message-text :deep(.ai-table th),
.message-text :deep(.ai-table td),
.message-text :deep(table.ai-table th),
.message-text :deep(table.ai-table td) {
  border: 1px solid #b8c7d8;
  padding: 6px;
  font-size: 12px;
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
  vertical-align: top;
}

.message-text {
  display: block;
  max-width: 100%;
  text-align: left;
}

.message-text :deep(.ai-table-container) {
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: touch;
}

.message-text :deep(.ai-table-header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  white-space: nowrap;
}

.message-text :deep(.ai-table-count),
.message-text :deep(.ai-table-header .btn),
.message-text :deep(.ai-table-header button),
.message-text :deep(.ai-table-header a) {
  white-space: nowrap;
  overflow-wrap: normal;
}

.message-text :deep(.ai-table),
.message-text :deep(table.ai-table) {
  width: max-content !important;
  min-width: 760px !important;
}

@media (max-width: 768px) {
  .ai-chat-layout {
    height: calc(100vh - 74px);
  }

  .chat-sidebar {
    position: absolute;
    z-index: 5;
    width: calc(100% - 16px);
    max-height: calc(100vh - 90px);
  }

  .chat-main {
    width: 100%;
  }

  .message-content {
    max-width: 92%;
  }

  .message-content.table-message {
    max-width: 100%;
  }
}
</style>

<style>
.ai-chat-page .message-text .ai-table-container {
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: touch;
}

.ai-chat-page .message-text .ai-table-header {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  flex-wrap: nowrap !important;
  gap: 10px;
  overflow-x: auto !important;
  white-space: nowrap !important;
}

.ai-chat-page .message-text .ai-table-header > * {
  flex: 0 0 auto;
}

.ai-chat-page .message-text .ai-table,
.ai-chat-page .message-text table.ai-table {
  width: max-content !important;
  min-width: 760px !important;
  max-width: none !important;
  table-layout: auto !important;
}

.ai-chat-page .message-text .ai-table th,
.ai-chat-page .message-text .ai-table td,
.ai-chat-page .message-text table.ai-table th,
.ai-chat-page .message-text table.ai-table td {
  white-space: nowrap !important;
  word-break: normal !important;
  overflow-wrap: normal !important;
  text-align: left !important;
}
</style>
